<template>
  <div class="vx-row">
    <div class="vx-col w-full">
      <div class="flex justify-between flex-wrap">
        <h2>
          {{ $t('Title') }}
        </h2>

        <vs-chip
          :color="userHas2faEnabled ? 'success' : 'danger'">
          <feather-icon
            :icon="userHas2faEnabled? 'CheckIcon' : 'XCircleIcon'"
            svgClasses="h-5 w-5 mr-1" />
          <span>
            {{ $t(`$General.${userHas2faEnabled ? 'Enabled': 'Disabled'}`) }}
          </span>
        </vs-chip>
      </div>

      <p class="mt-5">
        {{ $t('Description') }}
      </p>

      <div class="mt-3">
        <vs-button
          v-if="!userHas2faEnabled"
          @click="showModal2FA=true"
          color="primary"
          type="filled">
          {{ $t('$General.Enable') }}
        </vs-button>

        <template v-else>
          <div class="mb-2 font-black">
            {{ $t('Disabled2FAMsg') }}
          </div>

          <password-input
            v-model="currentPassword"
            ref="currentPassword"
            class="w-full"
            :name="$t('Disabled2faAskPasswordInputName')"
            v-validate="'required|password'"
            :danger="errors.has($t('Disabled2faAskPasswordInputName')) || !!invalidPasswordError"
            :danger-text="errors.first($t('Disabled2faAskPasswordInputName')) || invalidPasswordError"
            val-icon-danger="clear"
            :label="$t('Disabled2faAskPasswordInputName')"
            required
            @input="invalidPasswordError = ''">
          </password-input>

          <div>
            <forgot-password-link @click="activeForgotPasswordModal = true"/>
          </div>
          <div class="mt-3">
            <vs-button
              id="user-account-profile-disable-2FA-button"
              ref="disabledButton"
              :disabled="!disabled2FAIsValid"
              color="danger"
              type="filled"
              class="mt-2 vs-con-loading__container"
              @click="confirmDisable2FA()">
              {{ $t('$General.Disable') }}
            </vs-button>

            <vs-button
              v-show="showCancel"
              class="ml-3"
              type="filled"
              color="gray"
              @click="cancelDisabled2FA()">
              {{ $t('$General.Cancel') }}
            </vs-button>
          </div>
        </template>
      </div>

      <vs-popup
        :title="$t('Setup2FAModalTitle')"
        :active.sync="showModal2FA">
        <transition name="zoom-fade">
          <setup-two-factor-authentication
            v-if="showModal2FA"
            @enabled="twoFactorAuthWasEnabled()"/>
        </transition>
      </vs-popup>

      <vs-popup
        :title="$t('Disable2FAByEmailModalTitle')"
        :active.sync="showModalDisable2FA">
        <transition name="zoom-fade">
          <disable-two-factor-authentication
            v-if="showModalDisable2FA"
            @close="showModalDisable2FA=false">
          </disable-two-factor-authentication>
        </transition>
      </vs-popup>

      <send-forgot-password-email-popup
        v-model="activeForgotPasswordModal"
      />

    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

// Components
import UserAccountProfileSecurity2FASetup from '@/views/modules/user/user-account/user-account-details/UserAccountProfileSecurity2FASetup.vue';
import UserAccountProfileSecurity2FADisable from '@/views/modules/user/user-account/user-account-details/UserAccountProfileSecurity2FADisable.vue';
import ForgotPasswordLink from '@/views/modules/_components/ForgotPasswordLink.vue';
import SendForgotPasswordEmailPopup from '@/views/pages/auth/components/SendForgotPasswordEmailPopup.vue';
import PasswordInput from '@/components/PasswordInput.vue';

// Services
import userService from '@/api/modules/user.service';

/**
 * Component to allow the user manage the 2FA
 *
 * @module views/modules/user-account/UserAccountProfileSecurity
 * @author Dilan Useche <dilan8810@gmail.com>
 *
 */
export default {
  name: 'UserAccountProfileSecurity',
  i18n: {
    messages: {
      en: {
        Title: 'Two-Factor Authentication (2FA)',
        Description: '2FA adds an extra layer of security by requiring two forms of verification when logging in. This helps protect your account even if your password is compromised. You can enable it using an authenticator app for added security.',
        Disabled2FAMsg: 'Enter your current password to disable the two-factor authentication.',
        Disabled2faAskPasswordInputName: 'Current password',
        Setup2FAModalTitle: 'Configure Authenticator App',
        TwoFactorAuthDisabledNotifyTitle: 'Two-factor authentication',
        TwoFactorAuthDisabledNotifyMsg: 'Two-factor authentication disabled successfully',
        Disable2FAByEmailModalTitle: 'Disable two-factor authentication',
        Disable2FAConfirmDialog: {
          Title: 'Disable two-factor authentication',
          Msg: 'If you disable two‑factor authentication, you\'ll need either your email and password or your Google account to sign in to GrapePerks.',
        },
      },
    },
  },
  components: {
    SetupTwoFactorAuthentication: UserAccountProfileSecurity2FASetup,
    DisableTwoFactorAuthentication: UserAccountProfileSecurity2FADisable,
    ForgotPasswordLink,
    SendForgotPasswordEmailPopup,
    PasswordInput,
  },
  data() {
    return {
      showModal2FA: false,
      currentPassword: '',
      invalidPasswordError: '',
      showModalDisable2FA: false,
      activeForgotPasswordModal: false,
    };
  },
  computed: {
    ...mapGetters({
      getUserFlag: 'auth/getUserFlag',
    }),
    disabled2FAIsValid() {
      return !this.errors.any()
        && this.currentPassword !== '';
    },
    showCancel() {
      return Object.keys(this.validationFields).length > 0
        ? Object.keys(this.validationFields).some(
          (key) => this.validationFields[key].changed,
        )
        : false;
    },
    userHas2faEnabled() {
      return this.getUserFlag('twoFactorAuthentication');
    },
    userHasPassword() {
      return this.getUserFlag('hasPassword');
    },
  },
  methods: {
    ...mapActions({
      disable2FAByPassword: 'auth/disable2FAByPassword',
    }),
    cancelDisabled2FA() {
      this.currentPassword = '';
      this.invalidPasswordError = '';
      this.$validator.reset();
    },
    confirmDisable2FA() {
      this.$showConfirmDeleteDialog({
        title: this.$t('Disable2FAConfirmDialog.Title'),
        text: this.$t('Disable2FAConfirmDialog.Msg'),
        accept: () => this.disable2FA(),
        acceptText: this.$t('$General.Confirm'),
        cancelText: this.$t('$General.Cancel'),
      });
      this.$refs.disabledButton.$el.blur();
    },
    async disable2FA() {
      const formIsValid = await this.$validator.validate();

      if (!formIsValid) {
        return;
      }

      this.$vs.loading({
        background: 'danger',
        color: 'white',
        container: '#user-account-profile-disable-2FA-button',
        scale: 0.45,
      });

      try {
        await this.disable2FAByPassword(this.currentPassword);

        this.$showSuccessActionNotification({
          title: this.$t('TwoFactorAuthDisabledNotifyTitle'),
          text: this.$t('TwoFactorAuthDisabledNotifyMsg'),
        });
      } catch (err) {
        if ((err.response.data.statusCode === 400 || err.response.data.statusCode === 401)
          && (err.response.data.wrongPassword || err.response.data.invalidCode)) {
          this.invalidPasswordError = err.response.data.message;
        }
      } finally {
        this.$vs.loading.close('#user-account-profile-disable-2FA-button > .con-vs-loading');
      }
    },
    twoFactorAuthWasEnabled() {
      this.showModal2FA = false;
      this.currentPassword = '';
      this.$validator.reset();
    },
    async sendEmailCodeToDisable2FA() {
      this.showModalDisable2FA = true;
      await userService.sentEmailCodeToDisabled2FA();
    },
  },
};
</script>
